import type { Control as ControlView, OptionId } from "src/api";
import { Explained } from "./Explained";
import { Button, Glyph } from "./styles";

/**
 * One control of the row, with its card.
 *
 * **The state is the caption.** A lit toggle is the brand wash (`Tile`'s armed
 * rule, derived the same way rather than minted twice); an off control is
 * dimmed `aria-disabled` and **still hoverable**, because the reason is the
 * whole content of an off control.
 *
 * Its own component so the `HoverCard` wraps exactly one element: the primitive
 * clones its child to attach the ref and the pointer handlers, so a fragment or
 * a conditional would be a card with nothing to hang off.
 */
export function Control({
  item,
  choose,
  beside
}: {
  item: ControlView;
  /** `session.choose`: what the control does is the engine's. */
  choose(option: OptionId): void;
  beside: HTMLElement | null;
}) {
  return (
    <Explained item={item} beside={beside}>
      <Button
        // Verbatim from the shelf: every one of these ids is API and a smoke or
        // a colocated test has read it.
        data-testid={item.id}
        // Only where the control is a *toggle*. On a plain action there is
        // nothing to be pressed, and a control that always says "false" is
        // worse than one that says nothing.
        aria-pressed={item.on}
        // Off is `aria-disabled`, never the native attribute — a native
        // `disabled` cannot be hovered, and the reason is the whole content of
        // an off control (`Tile`'s ruling, and `HoverCard`'s).
        aria-disabled={item.enabled ? undefined : true}
        onClick={item.enabled ? () => choose(item.option) : undefined}
      >
        {item.glyph && <Glyph className={item.card?.state}>{item.glyph}</Glyph>}
        {item.label}
      </Button>
    </Explained>
  );
}
